import React from 'react'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'

// Interface for LinkedIn reply from heyreach_linkedin_events
export interface LinkedInReply {
  id: string
  campaign_id?: string | null
  campaign_name?: string | null
  event_type?: string
  timestamp?: string
  lead_full_name?: string | null
  lead_email_address?: string | null
  lead_profile_url?: string | null
  sender_full_name?: string | null
  sender_email_address?: string | null
  sender_profile_url?: string | null
  connection_message?: string | null
  recent_messages?: any // JSONB
  data?: any // JSONB - full payload
  created_at?: string
  // Mapped fields (to match AutoReply interface for table compatibility)
  to_name?: string
  to_email?: string
  from_name?: string
  from_email?: string
  campaign_details?: string
  reply_count?: number
  response_category?: string | null
  reply_category_type?: string | null
  ai_model_used?: string | null
  categorized_at?: string | null
  reply_preview?: string
  time_replied?: string
  sdr_response_tracking_status?: string | null
  average_time_to_respond?: number | null
  lead_without_a_reply?: boolean | null
  first_sdr_response_timestamp?: string | null
  first_positive_reply_timestamp?: string | null
  last_sdr_poll_at?: string | null
  client_id?: number | null
}

interface LinkedInRepliesData {
  replies: LinkedInReply[]
  count: number
}

async function fetchLinkedInRepliesOptimized(options?: { limit?: number; offset?: number }): Promise<LinkedInRepliesData> {
  console.log('🔄 Fetching LinkedIn replies with optimized query (Month to Date)...')
  
  // Calculate Month to Date date range (UK timezone)
  const now = new Date()
  const ukNow = new Date(now.toLocaleString('en-US', { timeZone: 'Europe/London' }))
  const monthStart = new Date(ukNow.getFullYear(), ukNow.getMonth(), 1)
    .toISOString().split('T')[0]
  
  const ukDate = new Date().toLocaleDateString('en-GB', {
    timeZone: 'Europe/London',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).split('/').reverse().join('-')
  
  const tomorrow = new Date(new Date(ukDate).getTime() + 24 * 60 * 60 * 1000)
    .toISOString().split('T')[0]
  
  const limit = options?.limit || 100
  const offset = options?.offset || 0
  
  // Build base query - only get every_message_reply_received events with campaign_id
  let query = supabase
    .from('heyreach_linkedin_events')
    .select(`
      id,
      campaign_id,
      campaign_name,
      event_type,
      timestamp,
      lead_full_name,
      lead_email_address,
      lead_profile_url,
      sender_full_name,
      sender_email_address,
      sender_profile_url,
      connection_message,
      recent_messages,
      data,
      created_at,
      response_category,
      response_category_id,
      reply_category_type,
      reasoning,
      ai_model_used,
      categorized_at,
      reply_count,
      sdr_response_tracking_status,
      average_time_to_respond,
      lead_without_a_reply,
      first_sdr_response_timestamp,
      first_positive_reply_timestamp,
      last_sdr_poll_at
    `)
    .eq('event_type', 'every_message_reply_received')
    .not('campaign_id', 'is', null)
    // Filter by Month to Date using created_at
    .gte('created_at', monthStart)
    .lt('created_at', tomorrow)
    .order('timestamp', { ascending: false })
  
  // Apply pagination if limit is specified
  if (limit && limit < 10000) {
    query = query.range(offset, offset + limit - 1)
  }
  
  console.log(`📊 Querying heyreach_linkedin_events: limit=${limit}, offset=${offset}, monthStart=${monthStart}`)
  const startTime = performance.now()
  
  const { data, error } = await query
  
  const endTime = performance.now()
  console.log(`⏱️  Query completed in ${Math.round(endTime - startTime)}ms`)

  if (error) {
    console.error('❌ Error fetching LinkedIn replies:', error)
    throw error
  }

  console.log(`✅ Fetched ${data?.length || 0} LinkedIn replies from heyreach_linkedin_events (offset: ${offset}, limit: ${limit})`)

  // Get all unique campaign_ids to resolve client_id via campaign variations
  const uniqueCampaignIds = [...new Set((data || []).map((r: any) => r.campaign_id).filter(Boolean))]
  const campaignToClientMap = new Map<string, number | null>()
  
  if (uniqueCampaignIds.length > 0) {
    // Resolve client_id via campaign variations logic:
    // heyreach_linkedin_events.campaign_id -> ab_test_variants.heyreach_campaign_id -> ab_test_segments.id -> campaigns.id -> clients.id
    const { data: variants } = await supabase
      .from('ab_test_variants')
      .select('heyreach_campaign_id, segment_id')
      .in('heyreach_campaign_id', uniqueCampaignIds)
      .eq('channel', 'linkedin')
      .not('heyreach_campaign_id', 'is', null)
    
    if (variants && variants.length > 0) {
      const segmentIds = [...new Set(variants.map(v => v.segment_id).filter(Boolean))]
      
      const { data: segments } = await supabase
        .from('ab_test_segments')
        .select('id, campaign_id')
        .in('id', segmentIds)
      
      if (segments && segments.length > 0) {
        const campaignIds = [...new Set(segments.map(s => s.campaign_id).filter(Boolean))]
        
        const { data: campaigns } = await supabase
          .from('campaigns')
          .select('id, client_id')
          .in('id', campaignIds)
        
        if (campaigns) {
          // Build mapping: heyreach_campaign_id -> client_id
          const segmentToCampaignMap = new Map(segments.map(s => [s.id, s.campaign_id]))
          const campaignToClientMapInternal = new Map(campaigns.map(c => [c.id, c.client_id]))
          
          variants.forEach(variant => {
            const segmentId = variant.segment_id
            const campaignId = segmentToCampaignMap.get(segmentId)
            if (campaignId) {
              const clientId = campaignToClientMapInternal.get(campaignId)
              if (variant.heyreach_campaign_id) {
                campaignToClientMap.set(variant.heyreach_campaign_id, clientId || null)
              }
            }
          })
        }
      }
    }
  }

  // Transform and map data to match AutoReply interface
  const mappedReplies: LinkedInReply[] = (data || []).map((item: any) => {
    // Extract reply message from recent_messages where is_reply = true
    let replyMessage = ''
    
    // FIXED: Use reply_count from database instead of calculating from recent_messages
    // The reply_count column is now correctly calculated by categorize-linkedin-reply Edge Function
    // which queries the database for all previous reply events for the same lead
    let replyCount = item.reply_count || 0;
    
    // If reply_count is not set in database, fallback to calculating from recent_messages
    // (for backwards compatibility with old records)
    if (!replyCount && item.recent_messages && Array.isArray(item.recent_messages)) {
      const replyMessages = item.recent_messages.filter((msg: any) => msg.is_reply === true)
      replyCount = replyMessages.length
    }
    
    // Extract reply message text for preview
    if (item.recent_messages && Array.isArray(item.recent_messages)) {
      const replyMessages = item.recent_messages.filter((msg: any) => msg.is_reply === true)
      if (replyMessages.length > 0) {
        // Get the most recent reply
        const latestReply = replyMessages[replyMessages.length - 1]
        replyMessage = latestReply.message || latestReply.text || ''
      }
    }
    
    // If no reply found in recent_messages, try data.message
    if (!replyMessage && item.data?.message) {
      replyMessage = item.data.message
    }
    
    // If still no reply, use connection_message as fallback
    if (!replyMessage && item.connection_message) {
      replyMessage = item.connection_message
    }

    return {
      id: item.id,
      campaign_id: item.campaign_id,
      campaign_name: item.campaign_name,
      event_type: item.event_type,
      timestamp: item.timestamp,
      lead_full_name: item.lead_full_name,
      lead_email_address: item.lead_email_address,
      lead_profile_url: item.lead_profile_url,
      sender_full_name: item.sender_full_name,
      sender_email_address: item.sender_email_address,
      sender_profile_url: item.sender_profile_url,
      connection_message: item.connection_message,
      recent_messages: item.recent_messages,
      data: item.data,
      created_at: item.created_at,
      // Mapped fields for table compatibility
      to_name: item.lead_full_name || null,
      to_email: item.lead_email_address || null,
      from_name: item.sender_full_name || null,
      from_email: item.sender_email_address || null,
      campaign_details: item.campaign_name || null,
      reply_count: replyCount,
      response_category: item.response_category || null,
      reply_category_type: item.reply_category_type || null,
      ai_model_used: item.ai_model_used || null,
      categorized_at: item.categorized_at || null,
      reasoning: item.reasoning || null,
      reply_preview: replyMessage,
      time_replied: item.timestamp || item.created_at,
      sdr_response_tracking_status: item.sdr_response_tracking_status || null,
      average_time_to_respond: item.average_time_to_respond || null,
      lead_without_a_reply: item.lead_without_a_reply || null,
      first_sdr_response_timestamp: item.first_sdr_response_timestamp || null,
      first_positive_reply_timestamp: item.first_positive_reply_timestamp || null,
      last_sdr_poll_at: item.last_sdr_poll_at || null,
      client_id: item.campaign_id ? campaignToClientMap.get(item.campaign_id) || null : null,
    }
  })

  // Get count separately (non-blocking, can be slow but doesn't delay data display)
  let totalCount = mappedReplies.length
  let countError: any = null
  
  // Only fetch count if we got data (optimistic count)
  if (mappedReplies.length > 0) {
    // Fetch count in background - don't wait for it (with Month to Date filter)
    supabase
      .from('heyreach_linkedin_events')
      .select('id', { count: 'exact', head: true })
      .eq('event_type', 'every_message_reply_received')
      .not('campaign_id', 'is', null)
      .gte('created_at', monthStart)
      .lt('created_at', tomorrow)
      .then(({ count }) => {
        if (count !== null) {
          console.log(`📊 Month to Date LinkedIn count updated: ${count}`)
          // Count will be updated on next refetch
        }
      })
      .catch((err) => {
        console.warn('⚠️ LinkedIn count query failed (non-critical):', err)
      })
    
    // Use optimistic count based on data length for now
    // If we got a full page, estimate there are more
    totalCount = mappedReplies.length === limit ? limit * 2 : mappedReplies.length
  }

  if (countError) {
    console.warn('⚠️ Error getting LinkedIn count (non-fatal):', countError)
    // Continue with data even if count fails
  }

  return {
    replies: mappedReplies,
    count: totalCount
  }
}

export function useLinkedInReplies() {
  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['linkedin-replies'],
    queryFn: () => fetchLinkedInRepliesOptimized(),
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: true,
    refetchOnMount: true,
  })

  return {
    replies: data?.replies || [],
    count: data?.count || 0,
    isLoading,
    error: error ? (error instanceof Error ? error.message : 'Unknown error') : null,
    refetch,
    isFetching,
  }
}
